import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import OrganisationBreadCrumb from 'shared/components/bread-crumb/organisation-bread-crumb/organisation-bread-crumb';
import PopUp from 'shared/components/pop-up/pop-up';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import { isG2Device } from 'shared/models/device-type';
import DeviceDetailsProviderContext from 'shared/providers/device-details/contexts/device-details-provider-context';
import DevicesProviderContext from 'shared/providers/devices/contexts/devices-provider-context';

import useDeviceSettingsOrganisation from 'features/device/device-details/views/device-details-settings/components/hooks/use-device-settings-organisation';
import OrganisationPicker from 'features/organisation/views/organisation-picker/organisation-picker';
import styles from './device-settings-organisation.module.scss';

const DeviceSettingsOrganisation = (): React.JSX.Element => {
  const { t } = useTranslation('deviceDetails');

  const { hasPermission } = usePermissionAuthorization();

  const { allowUnlinking } = use(DevicesProviderContext);

  const { linkedOrganisationErrorMessage, currentDevice } = use(DeviceDetailsProviderContext);

  const {
    field,
    currentViewingMode,
    upstreamParentBranch,
    currentGatewayId,
    currentSystemId,

    isOrganisationPickerOpen,
    isLinkErrorPopupOpen,
    unlinkConfirmationWarningOpen,
    unlinkWarningConfirmed,

    setUnlinkConfirmationWarningOpen,
    setIsLinkErrorPopupOpen,
    setOrganisationUnit,
    setIsOrganisationPickerOpen,
    handleOnUnlinkOrganisationUnit,
    handleWarningConfirmed,
  } = useDeviceSettingsOrganisation();

  const isG2PairedUnlinkedDevice = !!currentDevice?.systemId && !currentDevice.organisationUnitId && isG2Device(currentDevice.type);
  const isDeviceLinkedToSystem = !!currentDevice?.systemId;
  const isDeviceSystemLinkedToOrganisationUnit = currentDevice?.system?.organisationUnitId;
  const isDeviceLinkedToGateway = !!currentDevice?.gatewayId;
  const isDeviceGatewayLinkedToOrganisationUnit = currentDevice?.gateway?.organisationUnitId;

  const toolTipTitle = (): string => {
    if (!currentGatewayId && !currentSystemId) {
      return t('settings.firstSelectGateway');
    }

    if (unlinkWarningConfirmed) {
      return t('settings.g2DeviceAlreadyUnlinkedWarning');
    }

    if (isG2PairedUnlinkedDevice) {
      return t('settings.g2PairedUnlinkedDeviceTooltip');
    }

    return '';
  };

  return (
    <>
      {field.value ? (
        <div className={styles.breadCrumbContainer}>
          <OrganisationBreadCrumb rootNodeId={field.value} upstreamParentBranch={upstreamParentBranch} />
        </div>
      ) : (
        <div>
          <Typography variant="subtitle1">{t('settings.noOrganisationUnitLinkedLabel')}</Typography>
          {isDeviceLinkedToSystem && !isDeviceSystemLinkedToOrganisationUnit && (
            <Typography variant="subtitle1">{t('settings.noOrganisationUnitLinkedToSystemLabel')}</Typography>
          )}
          {isDeviceLinkedToGateway && !isDeviceGatewayLinkedToOrganisationUnit && (
            <Typography variant="subtitle1">{t('settings.noOrganisationUnitLinkedToGatewayLabel')}</Typography>
          )}
        </div>
      )}
      {currentViewingMode !== 'viewing' &&
        allowUnlinking &&
        hasPermission(Permission.LinkDevice) &&
        (field.value ? (
          <Button
            variant="outlined"
            onClick={() => handleOnUnlinkOrganisationUnit(field)}
            startIcon={<LinkOffIcon />}
            data-testid="unlinkOrganisationButton"
          >
            {t('settings.unlinkOrganisationUnitButton')}
          </Button>
        ) : (
          <>
            {(isDeviceSystemLinkedToOrganisationUnit || isDeviceGatewayLinkedToOrganisationUnit || currentViewingMode === 'creation') && (
              <Tooltip title={toolTipTitle()}>
                <span className={styles.tooltipWrapper}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsOrganisationPickerOpen(true)}
                    startIcon={<LinkIcon />}
                    data-testid="linkOrganisationButton"
                    disabled={(!currentGatewayId && !currentSystemId) || unlinkWarningConfirmed || isG2PairedUnlinkedDevice}
                  >
                    {t('settings.linkOrganisationUnitButton')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        ))}

      <OrganisationPicker
        isOpen={isOrganisationPickerOpen}
        onClose={() => setIsOrganisationPickerOpen(false)}
        onOrganisationUnitSelected={(selectedNode, upstreamParentBranch) => setOrganisationUnit(field, selectedNode, upstreamParentBranch)}
        sessionStorageKey={FilterSessionStorageKeys.DEVICE_ORGANISATION_PICKER_EXPANDED_NODES}
      />

      {linkedOrganisationErrorMessage && (
        <Typography color="error" variant="subtitle1">
          {linkedOrganisationErrorMessage}
        </Typography>
      )}

      <PopUp
        isOpen={isLinkErrorPopupOpen}
        title={t('linkDeviceError.title', { deviceType: t(`linkDeviceError.deviceType.${currentDevice?.type ?? ''}`) })}
        body={t('linkDeviceError.bodyText', { deviceType: t(`linkDeviceError.deviceType.${currentDevice?.type ?? ''}`).toLowerCase() })}
        primaryButtonText={t('linkDeviceError.confirmButton')}
        handleOnClose={() => setIsLinkErrorPopupOpen(false)}
        primaryButtonAction={() => setIsLinkErrorPopupOpen(false)}
      />

      <PopUp
        isOpen={unlinkConfirmationWarningOpen}
        title={t('unlinkConfirmationWarning.title')}
        body={t('unlinkConfirmationWarning.bodyText')}
        primaryButtonText={t('unlinkConfirmationWarning.confirmButton')}
        secondaryButtonText={t('unlinkConfirmationWarning.backButton')}
        handleOnClose={() => setUnlinkConfirmationWarningOpen(false)}
        primaryButtonAction={() => handleWarningConfirmed()}
        secondaryButtonAction={() => setUnlinkConfirmationWarningOpen(false)}
      />
    </>
  );
};

export default DeviceSettingsOrganisation;
